// Pure data layer for the Analytics view — kept out of the React
// component so the maths can be unit-tested without dragging in
// MSAL / browser-only imports through the component tree.
import type {
  JobDto,
  SpoolDto,
  StageDefDto,
} from "../../../../../../shared/types/production-schedule";
import {
  denseStages,
  inferWeldBayKey,
} from "../lib/stageHelpers";

export function monthKey(iso: string | null): string {
  return iso ? iso.slice(0, 7) : "";
}

export function monthLabel(d: Date): string {
  return d
    .toLocaleDateString("en-AU", { month: "short", year: "2-digit" })
    .replace(/\./g, "")
    .toUpperCase();
}

export function statusClass(s: string | null | undefined): string {
  return (s ?? "").toLowerCase().replace(/[^a-z]/g, "");
}

export interface Month {
  k: string;
  label: string;
  spoolCount: number;
  completed: number;
  remaining: number;
  total: number;
  isCurrent: boolean;
  isFuture: boolean;
  isPast: boolean;
}

export interface CustomerRow {
  name: string;
  total: number;
  count: number;
  completed: number;
}

export interface StatusRow {
  s: string;
  total: number;
  count: number;
}

// One row per spool. share = portion of the parent job's order
// value assigned to this spool; completionPct in [0,1] driven by
// stage progress; dueDate falls back to job.dueDate when the
// spool has no own due date; completedDate is the latest
// non-skipped stage doneDate when every active stage is complete.
export interface SpoolEntry {
  jobId: number;
  jobNumber: string;
  jobName: string;
  customer: string;
  status: string;
  spoolId: number;
  spoolNum: string;
  share: number;
  completionPct: number;
  dueDate: string | null;
  completed: boolean;
  completedDate: string | null;
}

export interface UpcomingRow {
  jobId: number;
  jobNumber: string;
  jobName: string;
  customer: string;
  status: string;
  spoolNum: string;
  dueDate: string | null;
  share: number;
  completionPct: number;
}

export interface Computed {
  totalWorkbook: number;
  active: JobDto[];
  missingValue: number;
  curMonthSpools: number;
  completedMTD: number;
  remainingCur: number;
  projCur: number;
  projNext: number;
  curLabel: string;
  nextLabel: string;
  curShort: string;
  nextShort: string;
  months: Month[];
  maxMonth: number;
  customers: CustomerRow[];
  statuses: StatusRow[];
  upcoming: UpcomingRow[];
  curKey: string;
  nextKey: string;
}

// Status order matches the prototype's pipeline grouping. Cancelled
// is excluded because active workbook ignores it everywhere else.
const STATUS_ORDER = [
  "New",
  "Scheduled",
  "In Progress",
  "Review",
  "IFA",
  "Complete",
];

// Stage progress for a single spool. denseStages overlays the
// runtime catalogue onto the persisted rows so the denominator
// reflects the canonical workflow (e.g. 10 active stages once non-
// chosen weld bays drop out) instead of just the rows the server
// has lazy-seeded. Without the overlay, a spool with 3 persisted
// "complete" rows would report 100% complete while 7 untouched
// stages still lie ahead. Skipped stages (non-chosen weld bays,
// archived catalogue entries, user-skipped rows) are excluded from
// both numerator and denominator.
export function spoolProgress(
  spool: SpoolDto,
  jobBay: number | null,
  catalogue: StageDefDto[],
): { pct: number; completed: boolean; completedDate: string | null } {
  const bay = inferWeldBayKey(spool.stages, jobBay);
  const dense = denseStages(spool.stages, bay, undefined, catalogue);
  const activeStages = dense.filter((s) => s.status !== "skipped");
  if (activeStages.length === 0) {
    return { pct: 0, completed: false, completedDate: null };
  }
  const completedStages = activeStages.filter((s) => s.status === "complete");
  const allDone = completedStages.length === activeStages.length;
  let latestDone: string | null = null;
  if (allDone) {
    for (const s of completedStages) {
      if (s.doneDate && (!latestDone || s.doneDate > latestDone)) {
        latestDone = s.doneDate;
      }
    }
  }
  return {
    pct: completedStages.length / activeStages.length,
    completed: allDone,
    completedDate: latestDone,
  };
}

export function entriesForJob(job: JobDto, catalogue: StageDefDto[]): SpoolEntry[] {
  const orderValue = Number(job.orderValue) || 0;
  const out: SpoolEntry[] = [];
  if (job.spools.length === 0) {
    // No spools persisted yet — fall back to a single synthetic
    // entry so the job's order value still shows up against its
    // due date. Status drives completion: "Complete" jobs count as
    // delivered.
    const isComplete = job.status === "Complete";
    out.push({
      jobId: job.id,
      jobNumber: job.jobNumber,
      jobName: job.name,
      customer: job.customer ?? "—",
      status: job.status,
      spoolId: -1,
      spoolNum: "—",
      share: orderValue,
      completionPct: isComplete ? 1 : 0,
      dueDate: job.dueDate,
      completed: isComplete,
      completedDate: isComplete ? job.dueDate : null,
    });
    return out;
  }
  // Equal-weight share. Hours-weighted would skew toward heavier
  // spools but we don't reliably have estimated hours filled in
  // at this stage of the workflow — equal split keeps the metric
  // explainable.
  const share = orderValue / job.spools.length;
  for (const spool of job.spools as SpoolDto[]) {
    const prog = spoolProgress(spool, job.bay, catalogue);
    out.push({
      jobId: job.id,
      jobNumber: job.jobNumber,
      jobName: job.name,
      customer: job.customer ?? "—",
      status: job.status,
      spoolId: spool.id,
      spoolNum: spool.num,
      share,
      completionPct: prog.pct,
      dueDate: spool.dueDate ?? job.dueDate,
      completed: prog.completed,
      completedDate: prog.completedDate,
    });
  }
  return out;
}

export function compute(jobs: JobDto[], catalogue: StageDefDto[]): Computed {
  const today = new Date();
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  const curKey = fmt(today);
  const nextDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const nextKey = fmt(nextDate);

  const active = jobs.filter((j) => j.status !== "Cancelled");
  const entries = active.flatMap((j) => entriesForJob(j, catalogue));

  const totalWorkbook = entries.reduce((a, e) => a + e.share, 0);

  // Completed-MTD counts a spool only when every active stage is
  // complete AND the latest stage doneDate is in the current
  // month. Partial in-progress work shows up in projCur as a
  // "completed-this-month" segment of the bar but not in the
  // top-line Completed-MTD KPI.
  const completedMTD = entries
    .filter((e) => e.completed && monthKey(e.completedDate) === curKey)
    .reduce((a, e) => a + e.share, 0);

  // projCur = every spool whose dueDate falls in the current
  // month. remainingCur subtracts completion-weighted credit so
  // a 60%-done spool due this month leaves 40% × share as
  // remaining work.
  const curMonthEntries = entries.filter((e) => monthKey(e.dueDate) === curKey);
  const projCur = curMonthEntries.reduce((a, e) => a + e.share, 0);
  const curMonthDelivered = curMonthEntries.reduce(
    (a, e) => a + e.share * e.completionPct,
    0,
  );
  const remainingCur = projCur - curMonthDelivered;
  const projNext = entries
    .filter((e) => monthKey(e.dueDate) === nextKey)
    .reduce((a, e) => a + e.share, 0);

  const missingValue = jobs.filter(
    (j) => j.status !== "Cancelled" && !(Number(j.orderValue) || 0),
  ).length;

  const months: Month[] = [];
  for (let i = -3; i <= 2; i += 1) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    const k = fmt(d);
    const monthEntries = entries.filter((e) => monthKey(e.dueDate) === k);
    const c = monthEntries.reduce((a, e) => a + e.share * e.completionPct, 0);
    const r = monthEntries.reduce(
      (a, e) => a + e.share * (1 - e.completionPct),
      0,
    );
    months.push({
      k,
      label: monthLabel(d),
      spoolCount: monthEntries.length,
      completed: c,
      remaining: r,
      total: c + r,
      isCurrent: k === curKey,
      isFuture: k > curKey,
      isPast: k < curKey,
    });
  }
  const maxMonth = Math.max(1, ...months.map((m) => m.total));

  // Customer rollup uses entries (so partial credit applies) and
  // counts unique jobs, not spools, since the workshop thinks in
  // jobs when picking a customer to chase.
  const byCust = new Map<string, CustomerRow & { jobIds: Set<number> }>();
  for (const e of entries) {
    const row = byCust.get(e.customer)
      ?? { name: e.customer, total: 0, count: 0, completed: 0, jobIds: new Set() };
    row.total += e.share;
    row.completed += e.share * e.completionPct;
    row.jobIds.add(e.jobId);
    byCust.set(e.customer, row);
  }
  const customers = Array.from(byCust.values())
    .map(({ jobIds, ...rest }) => ({ ...rest, count: jobIds.size }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 8);

  // Status rollup stays job-level — value sums spool shares by the
  // parent job's status so a job that's "In Progress" still
  // contributes the whole orderValue under that bucket.
  const byStatus = new Map<string, StatusRow & { jobIds: Set<number> }>();
  for (const e of entries) {
    const row = byStatus.get(e.status)
      ?? { s: e.status, total: 0, count: 0, jobIds: new Set() };
    row.total += e.share;
    row.jobIds.add(e.jobId);
    byStatus.set(e.status, row);
  }
  const statuses = STATUS_ORDER
    .filter((s) => byStatus.has(s))
    .map((s) => {
      const r = byStatus.get(s)!;
      return { s: r.s, total: r.total, count: r.jobIds.size };
    });

  // Upcoming pipeline is now spool-level — workshop wants to see
  // which spools are due next, not just which jobs. Sorted by
  // due date ascending, top 12. Empty dueDate spools sort to the
  // bottom under the "9999" sentinel.
  const upcoming: UpcomingRow[] = entries
    .filter((e) => monthKey(e.dueDate) === curKey || monthKey(e.dueDate) === nextKey)
    .sort((a, b) => (a.dueDate ?? "9999").localeCompare(b.dueDate ?? "9999"))
    .slice(0, 12)
    .map((e) => ({
      jobId: e.jobId,
      jobNumber: e.jobNumber,
      jobName: e.jobName,
      customer: e.customer,
      status: e.status,
      spoolNum: e.spoolNum,
      dueDate: e.dueDate,
      share: e.share,
      completionPct: e.completionPct,
    }));

  const curLabel = today.toLocaleDateString("en-AU", { month: "long", year: "numeric" });
  const nextLabel = nextDate.toLocaleDateString("en-AU", { month: "long", year: "numeric" });
  const curShort = curLabel.split(" ")[0]?.toUpperCase() ?? "";
  const nextShort = nextLabel.split(" ")[0]?.toUpperCase() ?? "";

  return {
    totalWorkbook, active, missingValue,
    curMonthSpools: curMonthEntries.length,
    completedMTD, remainingCur, projCur, projNext,
    curLabel, nextLabel, curShort, nextShort,
    months, maxMonth, customers, statuses, upcoming,
    curKey, nextKey,
  };
}
